import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Mail, MapPin, Phone, Clock } from "lucide-react";
import { RichText } from "@/components/RichText";
import { Button } from "@/components/ui/button";
import { Wrap, SectionHead } from "@/website/sections/Basic";
import { BookingWidget, EnquiryFields, submitEnquiry } from "@/website/BookingWidget";

const EnquiryForm = ({ testId }) => {
  const [form, setForm] = useState({ consent: false });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await submitEnquiry(form); setSent(true); } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Couldn't send - please try again");
    } finally { setBusy(false); }
  };
  if (sent) return <div className="rounded-2xl bg-white p-8 text-center text-slate-800" data-testid={`${testId}-sent`}><CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" /><p className="mt-3 font-semibold">Thanks! We'll get back to you within 1-2 working days.</p></div>;
  return (
    <form onSubmit={submit} className="rounded-2xl bg-white p-5 sm:p-6 shadow-xl space-y-4" data-testid={`${testId}-form`}>
      <EnquiryFields form={form} set={setForm} withChild={false} testId={testId} />
      <Button type="submit" disabled={busy || !form.consent} className="w-full rounded-full h-11 bg-[var(--site-primary)]" data-testid={`${testId}-submit`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send enquiry"}</Button>
    </form>
  );
};

const Row = ({ icon: Icon, children }) => <div className="flex gap-3 items-start"><Icon className="w-5 h-5 mt-0.5 text-[var(--site-accent)] shrink-0" /><div>{children}</div></div>;

export const ContactSection = ({ d, testId }) => (
  <Wrap theme="dark" testId={testId}>
    <div className="grid lg:grid-cols-2 gap-10 items-start">
      <div className="space-y-5">
        <h2 className="text-3xl sm:text-4xl font-extrabold font-['Manrope']">{d.heading}</h2>
        {d.text && <RichText html={d.text} className="opacity-80 text-lg" />}
        {d.email && <Row icon={Mail}><a href={`mailto:${d.email}`} className="hover:underline">{d.email}</a></Row>}
        {d.phone && <Row icon={Phone}><a href={`tel:${d.phone}`} className="hover:underline">{d.phone}</a></Row>}
        {d.address && <Row icon={MapPin}><span className="whitespace-pre-line">{d.address}</span></Row>}
        {d.hours && <Row icon={Clock}><RichText html={d.hours} className="text-sm opacity-90" /></Row>}
      </div>
      {d.show_form !== false && <EnquiryForm testId={`${testId}-enquiry`} />}
    </div>
  </Wrap>
);

export const BookingSection = ({ d, testId }) => (
  <Wrap theme="accent" testId={testId} narrow>
    <SectionHead heading={d.heading} subheading={d.text} />
    <BookingWidget testId={`${testId}-widget`} />
  </Wrap>
);
